import axios from 'axios';
import { adminAuthService } from './adminAuth';
import { adminApiUrl } from '../config/api';

export interface PlatformConfig {
  configKey: string;
  configValue: string | number | boolean | Record<string, unknown>;
  description?: string;
  category: 'pricing' | 'commission' | 'booking' | 'general' | 'security' | 'notification';
  updatedBy?: string;
  updatedAt: string;
  createdAt: string;
}

export interface ServiceCategory {
  categoryId: string;
  name: string;
  description?: string;
  icon?: string;
  isActive: boolean;
  displayOrder: number;
  parentCategoryId?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationTemplate {
  templateId: string;
  templateType: string;
  title: string;
  body: string;
  variables?: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  updatedBy?: string;
}

export interface EmailTemplate {
  templateId: string;
  templateType: string;
  subject: string;
  htmlBody: string;
  textBody?: string;
  variables?: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  updatedBy?: string;
}

export interface AuditLog {
  logId: string;
  adminId: string;
  adminEmail: string;
  actionType: string;
  resourceType: string;
  resourceId?: string;
  details?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  timestamp: string;
}

export interface SystemHealth {
  timestamp: string;
  status: 'healthy' | 'degraded' | 'down';
  metrics: {
    database: { status: string; responseTime?: number };
    api: { status: string; averageResponseTime?: number; errorRate?: number };
    lambda: { status: string; averageDuration?: number; errorRate?: number };
  };
  alerts?: string[];
}

const getAuthHeaders = () => {
  const token = adminAuthService.getToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

// Platform Config
export const getPlatformConfig = async (configKey: string): Promise<PlatformConfig> => {
  const response = await axios.get(`${adminApiUrl}/admin/system-settings/config/${configKey}`, {
    headers: getAuthHeaders(),
  });
  return response.data.data.config;
};

export const listPlatformConfigs = async (category?: string): Promise<PlatformConfig[]> => {
  const params = category ? { category } : {};
  const response = await axios.get(`${adminApiUrl}/admin/system-settings/config`, {
    headers: getAuthHeaders(),
    params,
  });
  return response.data.data.configs || [];
};

export const createPlatformConfig = async (config: Partial<PlatformConfig>): Promise<PlatformConfig> => {
  const response = await axios.post(`${adminApiUrl}/admin/system-settings/config`, config, {
    headers: getAuthHeaders(),
  });
  return response.data.data.config;
};

export const updatePlatformConfig = async (configKey: string, updates: Partial<PlatformConfig>): Promise<PlatformConfig> => {
  const response = await axios.put(`${adminApiUrl}/admin/system-settings/config/${configKey}`, updates, {
    headers: getAuthHeaders(),
  });
  return response.data.data.config;
};

export const deletePlatformConfig = async (configKey: string): Promise<void> => {
  await axios.delete(`${adminApiUrl}/admin/system-settings/config/${configKey}`, {
    headers: getAuthHeaders(),
  });
};

// Service Categories
export const getServiceCategory = async (categoryId: string): Promise<ServiceCategory> => {
  const response = await axios.get(`${adminApiUrl}/admin/system-settings/service-categories/${categoryId}`, {
    headers: getAuthHeaders(),
  });
  return response.data.data.category;
};

export const listServiceCategories = async (activeOnly?: boolean): Promise<ServiceCategory[]> => {
  const params = activeOnly ? { activeOnly: 'true' } : {};
  const response = await axios.get(`${adminApiUrl}/admin/system-settings/service-categories`, {
    headers: getAuthHeaders(),
    params,
  });
  return response.data.data.categories || [];
};

export const createServiceCategory = async (category: Partial<ServiceCategory>): Promise<ServiceCategory> => {
  const response = await axios.post(`${adminApiUrl}/admin/system-settings/service-categories`, category, {
    headers: getAuthHeaders(),
  });
  return response.data.data.category;
};

export const updateServiceCategory = async (categoryId: string, updates: Partial<ServiceCategory>): Promise<ServiceCategory> => {
  const response = await axios.put(`${adminApiUrl}/admin/system-settings/service-categories/${categoryId}`, updates, {
    headers: getAuthHeaders(),
  });
  return response.data.data.category;
};

export const deleteServiceCategory = async (categoryId: string): Promise<void> => {
  await axios.delete(`${adminApiUrl}/admin/system-settings/service-categories/${categoryId}`, {
    headers: getAuthHeaders(),
  });
};

export interface ServiceCategoryImportItem {
  categoryId: string;
  name: string;
  displayOrder: number;
  isActive?: boolean;
  description?: string;
}

export const importServiceCategories = async (items: ServiceCategoryImportItem[]): Promise<{ imported: number }> => {
  try {
    const response = await axios.post(
      `${adminApiUrl}/admin/system-settings/service-categories`,
      { items },
      { headers: getAuthHeaders() }
    );
    return { imported: response.data.data.imported as number };
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response?.status === 400) {
      const data = err.response.data as {
        error?: { message?: string; data?: { errors?: { index: number; message: string }[] } };
      };
      const rowErrs = data.error?.data?.errors;
      if (rowErrs?.length) {
        const msg = rowErrs
          .map((e) => {
            if (e.index < 0) return e.message;
            return `Row ${e.index + 1}: ${e.message}`;
          })
          .join('\n');
        throw new Error(msg);
      }
    }
    throw err;
  }
};

// Notification Templates
export const getNotificationTemplate = async (templateId: string): Promise<NotificationTemplate> => {
  const response = await axios.get(`${adminApiUrl}/admin/system-settings/notification-templates/${templateId}`, {
    headers: getAuthHeaders(),
  });
  return response.data.data.template;
};

export const listNotificationTemplates = async (templateType?: string, activeOnly?: boolean): Promise<NotificationTemplate[]> => {
  const params: Record<string, string> = {};
  if (templateType) params.templateType = templateType;
  if (activeOnly) params.activeOnly = 'true';
  const response = await axios.get(`${adminApiUrl}/admin/system-settings/notification-templates`, {
    headers: getAuthHeaders(),
    params,
  });
  return response.data.data.templates || [];
};

export const createNotificationTemplate = async (template: Partial<NotificationTemplate>): Promise<NotificationTemplate> => {
  const response = await axios.post(`${adminApiUrl}/admin/system-settings/notification-templates`, template, {
    headers: getAuthHeaders(),
  });
  return response.data.data.template;
};

export const updateNotificationTemplate = async (templateId: string, updates: Partial<NotificationTemplate>): Promise<NotificationTemplate> => {
  const response = await axios.put(`${adminApiUrl}/admin/system-settings/notification-templates/${templateId}`, updates, {
    headers: getAuthHeaders(),
  });
  return response.data.data.template;
};

export const deleteNotificationTemplate = async (templateId: string): Promise<void> => {
  await axios.delete(`${adminApiUrl}/admin/system-settings/notification-templates/${templateId}`, {
    headers: getAuthHeaders(),
  });
};

// Email Templates
export const getEmailTemplate = async (templateId: string): Promise<EmailTemplate> => {
  const response = await axios.get(`${adminApiUrl}/admin/system-settings/email-templates/${templateId}`, {
    headers: getAuthHeaders(),
  });
  return response.data.data.template;
};

export const listEmailTemplates = async (templateType?: string, activeOnly?: boolean): Promise<EmailTemplate[]> => {
  const params: Record<string, string> = {};
  if (templateType) params.templateType = templateType;
  if (activeOnly) params.activeOnly = 'true';
  const response = await axios.get(`${adminApiUrl}/admin/system-settings/email-templates`, {
    headers: getAuthHeaders(),
    params,
  });
  return response.data.data.templates || [];
};

export const createEmailTemplate = async (template: Partial<EmailTemplate>): Promise<EmailTemplate> => {
  const response = await axios.post(`${adminApiUrl}/admin/system-settings/email-templates`, template, {
    headers: getAuthHeaders(),
  });
  return response.data.data.template;
};

export const updateEmailTemplate = async (templateId: string, updates: Partial<EmailTemplate>): Promise<EmailTemplate> => {
  const response = await axios.put(`${adminApiUrl}/admin/system-settings/email-templates/${templateId}`, updates, {
    headers: getAuthHeaders(),
  });
  return response.data.data.template;
};

export const deleteEmailTemplate = async (templateId: string): Promise<void> => {
  await axios.delete(`${adminApiUrl}/admin/system-settings/email-templates/${templateId}`, {
    headers: getAuthHeaders(),
  });
};

// System Health
export const getSystemHealth = async (): Promise<SystemHealth> => {
  const response = await axios.get(`${adminApiUrl}/admin/system-settings/health`, {
    headers: getAuthHeaders(),
  });
  return response.data.data.health;
};

// Audit Logs
export const listAuditLogs = async (options?: {
  adminId?: string;
  actionType?: string;
  resourceType?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}): Promise<{ logs: AuditLog[]; lastEvaluatedKey?: Record<string, unknown>; count: number }> => {
  const response = await axios.get(`${adminApiUrl}/admin/system-settings/audit-logs`, {
    headers: getAuthHeaders(),
    params: options,
  });
  return response.data.data;
};

export const getAuditLog = async (logId: string): Promise<AuditLog> => {
  const response = await axios.get(`${adminApiUrl}/admin/system-settings/audit-logs/${logId}`, {
    headers: getAuthHeaders(),
  });
  return response.data.data.log;
};

// Data Export & Deletion
export const exportUserData = async (userId: string): Promise<Record<string, unknown>> => {
  const response = await axios.get(`${adminApiUrl}/admin/system-settings/export-user-data/${userId}`, {
    headers: getAuthHeaders(),
  });
  return response.data.data.userData;
};

export const deleteUserData = async (userId: string): Promise<void> => {
  await axios.delete(`${adminApiUrl}/admin/system-settings/delete-user-data/${userId}`, {
    headers: getAuthHeaders(),
  });
};

export const systemSettingsService = {
  getPlatformConfig,
  listPlatformConfigs,
  createPlatformConfig,
  updatePlatformConfig,
  deletePlatformConfig,
  getServiceCategory,
  listServiceCategories,
  createServiceCategory,
  updateServiceCategory,
  deleteServiceCategory,
  importServiceCategories,
  getNotificationTemplate,
  listNotificationTemplates,
  createNotificationTemplate,
  updateNotificationTemplate,
  deleteNotificationTemplate,
  getEmailTemplate,
  listEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  getSystemHealth,
  listAuditLogs,
  getAuditLog,
  exportUserData,
  deleteUserData,
};
